import { useState, useCallback, useEffect, useMemo } from "react";
import { useTenant } from "../../../hooks/useTenant";
import { toast } from "react-toastify";
import MemberService from "../../../services/member_service";
import ActivityService from "../../../services/activity_service";
import { initialMemberValues } from "../../../models/member";

/**
 * useMembersPage
 * Hook orquestrador para a página de Clientes (Members).
 */
export const useMembersPage = () => {
    const { idTenant, user, loading: tenantLoading } = useTenant();

    // Estados de Dados
    const [members, setMembers] = useState([]);
    const [activities, setActivities] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Estados do Formulário/Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formState, setFormState] = useState(initialMemberValues);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    /**
     * Carrega dados iniciais
     */
    const loadData = useCallback(async () => {
        if (!idTenant) return;
        setIsInitialLoading(true);

        try {
            const [membersData, activitiesData] = await Promise.all([
                MemberService.getMembers(idTenant),
                ActivityService.getActivities(idTenant)
            ]);

            setMembers(membersData);
            setActivities(activitiesData);
        } catch (error) {
            console.error("Erro ao carregar dados dos membros:", error);
            toast.error("Erro ao carregar clientes.");
        } finally {
            setIsInitialLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        if (!tenantLoading && idTenant) {
            loadData();
        }
    }, [tenantLoading, idTenant, loadData]);

    /**
     * Filtro de membros (Busca + Status)
     */
    const filteredMembers = useMemo(() => {
        let result = members;

        if (statusFilter !== "all") {
            result = result.filter(m => m.status === statusFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(m =>
                m.fullName?.toLowerCase().includes(term) ||
                m.email?.toLowerCase().includes(term) ||
                m.memberNumber?.includes(term)
            );
        }

        return result;
    }, [members, searchTerm, statusFilter]);

    /**
     * Handlers do Modal
     */
    const handleToggleModal = useCallback(() => {
        if (isModalOpen) {
            setFormState(initialMemberValues);
        }
        setIsModalOpen(prev => !prev);
    }, [isModalOpen]);

    const handleEdit = useCallback((member) => {
        setFormState(member);
        setIsModalOpen(true);
    }, []);

    const handleSave = useCallback(async (data) => {
        setIsSaving(true);
        try {
            await MemberService.save(data, idTenant, user, members);
            toast.success(data.id ? "Cliente atualizado!" : "Cliente cadastrado!");
            await loadData();
            handleToggleModal();
        } catch (error) {
            console.error("Erro ao salvar membro:", error);
            toast.error("Erro ao salvar cliente.");
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, members, loadData, handleToggleModal]);

    const handleDelete = useCallback(async (memberId, memberName) => {
        if (!window.confirm(`Tem certeza que deseja excluir ${memberName}?`)) return;

        try {
            await MemberService.delete(memberId, idTenant, user, memberName);
            toast.success("Cliente removido!");
            loadData();
        } catch (error) {
            console.error("Erro ao excluir membro:", error);
            toast.error("Erro ao excluir cliente.");
        }
    }, [idTenant, user, loadData]);

    return {
        // Dados
        members: filteredMembers,
        activities,
        isInitialLoading,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,

        // Modal/Form
        isModalOpen,
        setIsModalOpen,
        handleToggleModal,
        formState,
        setFormState,
        isSaving,
        handleSave,
        handleEdit,
        handleDelete,

        // Status
        isReady: !tenantLoading && !!idTenant
    };
};
